import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import removeOptionColorValue from '@rsc/utils/removeOptionColorValue';
import getPercentageDiscountPrice from '@rsc/utils/getPercentageDiscountPrice';
import generateProductSlug from '@rsc/utils/generateProductSlug';
import canApplyProStaffDiscount from '@rsc/utils/canApplyProStaffDiscount';
import { useUiComponentsInternationalContext } from '@rsc/static-translations';

import { CartContext } from '@rsc/contexts/CartContext';
import { CustomerContext } from '@rsc/contexts/CustomerContext';
import IconPlus from '../svgs/plus.svg';
import IconMinus from '../svgs/minus.svg';
import IconTrash from '../svgs/trash.svg';

import {
  Wrapper,
  LineItemDetails,
  LineItemQuantityChanger,
  QuantityControls,
  TotalPrice,
} from './LineItemStyles';

const decodeHtml = html => {
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
};

const IncreaseQuantityButton = styled.a`
  background: white;
  border: solid 2px ${({ theme }) => theme.colors.inputBorder};
  display: flex;
  margin: 0;
  padding: 0;
  line-height: 1.51;
  width: 2.75rem;
  align-items: center;
  justify-content: center;
  height: 2.5rem;
  cursor: pointer;
  transition: all 0.1s ease-out;
  &:hover {
    border: solid 2px ${({ hoverColor }) => hoverColor};
    box-shadow: 0 0 4px 0 ${({ hoverColor }) => hoverColor};
  }
`;

const DecreaseQuantityButton = styled(IncreaseQuantityButton)`
  &:hover {
    border: solid 2px
      ${({ theme, hoverColor, active }) =>
        active ? hoverColor : theme.colors.inputBorder};
    box-shadow: ${({ hoverColor, active }) =>
      active ? `0 0 4px 0 ${hoverColor}` : 'none'};
  }
`;

const TrashIcon = styled(IconTrash)`
  transition: all 0.15s ease-out;
`;

const LineItemDeleteButton = styled.button`
  width: 2rem;
  height: 2rem;
  position: absolute;
  right: 0;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  appearance: none;
  background: transparent;
  border: none;
  z-index: 2;
  ${TrashIcon} {
    fill: ${({ color }) => color};
    position: absolute;
  }

  &:hover {
    ${TrashIcon} {
      fill: ${({ hoverColor }) => hoverColor};
    }
  }
  &:focus {
    outline: 1px dotted #212121;
    outline: 5px auto -webkit-focus-ring-color;
  }
`;

const isEnterKeyPressed = event => event.charCode === 13;

function LineItem({
  id,
  highlightColor,
  textColor,
  variant,
  title,
  quantity,
  deleteLineItemHandler,
  decreaseQuantityHandler,
  increaseQuantityHandler,
  customAttributes,
}) {
  const context = useContext(CustomerContext);
  const { formatPrice } = useContext(CartContext);

  const { tt } = useUiComponentsInternationalContext();

  const tags = variant?.product?.tags;

  const compareAtPriceV2amount =
    variant?.compareAtPrice && parseFloat(variant?.compareAtPrice);

  const isProstaffNoSalePrice =
    (context?.isProStaff() &&
      canApplyProStaffDiscount(tags, compareAtPriceV2amount)) ||
    false;

  const isProstaffAndSalePrice =
    (context?.isProStaff() &&
      !canApplyProStaffDiscount(tags, compareAtPriceV2amount)) ||
    false;

  const variantPriceAmount = variant?.price.amount ?? 0;

  const productHandle = generateProductSlug({
    productHandle: variant?.product?.handle,
    productType: variant?.product?.productType || null,
  });

  return (
    <Wrapper>
      <LineItemDetails to={`/${productHandle}`}>
        {variant?.image?.src && (
          <img
            src={`${variant.image.src}&width=64`}
            alt={tt(s => s.cartCommon.lineItemThumbnailAlt, { title })}
          />
        )}
        <div>
          <h3>{decodeHtml(title)}</h3>
          {removeOptionColorValue(variant?.title) !== 'Default Title' && (
            <h4>{removeOptionColorValue(variant?.title)}</h4>
          )}
          {isProstaffNoSalePrice && (
            <p>
              {tt(s => s.cartCommon.priceEach, {
                price: formatPrice(
                  getPercentageDiscountPrice({
                    price: variantPriceAmount,
                    percentageDiscount:
                      process.env.GATSBY_PRO_STAFF_DISCOUNT_PERCENTAGE,
                  })
                ),
              })}
            </p>
          )}
          {isProstaffAndSalePrice && (
            <p>
              {tt(s => s.cartCommon.priceEach, {
                price: formatPrice(variantPriceAmount),
              })}
            </p>
          )}
          {!context?.isProStaff() && (
            <p>
              {tt(s => s.cartCommon.priceEach, {
                price: formatPrice(variantPriceAmount),
              })}
            </p>
          )}
        </div>
        <LineItemDeleteButton
          tabIndex={0}
          color={textColor}
          hoverColor={highlightColor}
          onClick={event => {
            event.preventDefault();
            deleteLineItemHandler(id);
          }}
          onKeyPress={event =>
            isEnterKeyPressed(event) && deleteLineItemHandler(id)
          }
        >
          <TrashIcon />
        </LineItemDeleteButton>
      </LineItemDetails>
      <LineItemQuantityChanger>
        <QuantityControls textColor={textColor} hoverColor={highlightColor}>
          <DecreaseQuantityButton
            tabIndex={0}
            hoverColor={highlightColor}
            active={quantity > 1}
            onClick={() => {
              if (quantity > 1) {
                decreaseQuantityHandler(id);
              }
            }}
            onKeyPress={event =>
              isEnterKeyPressed(event) &&
              quantity > 1 &&
              decreaseQuantityHandler(id)
            }
          >
            <IconMinus
              viewBox="0 0 50 50"
              width="20"
              height="20"
              stroke={quantity > 1 ? highlightColor : textColor}
            />
          </DecreaseQuantityButton>
          <div>{quantity}</div>
          <IncreaseQuantityButton
            tabIndex={0}
            hoverColor={highlightColor}
            onClick={() => {
              increaseQuantityHandler(id);
            }}
            onKeyPress={event =>
              isEnterKeyPressed(event) && increaseQuantityHandler(id)
            }
          >
            <IconPlus
              viewBox="0 0 50 50"
              width="28"
              height="28"
              stroke={highlightColor}
            />
          </IncreaseQuantityButton>
        </QuantityControls>
        {!context?.isProStaff() && (
          <TotalPrice>{formatPrice(variantPriceAmount * quantity)}</TotalPrice>
        )}
        {isProstaffAndSalePrice && (
          <TotalPrice>{formatPrice(variantPriceAmount * quantity)}</TotalPrice>
        )}
        {isProstaffNoSalePrice && (
          <TotalPrice>
            {formatPrice(
              getPercentageDiscountPrice({
                price: variantPriceAmount * quantity,
                percentageDiscount:
                  process.env.GATSBY_PRO_STAFF_DISCOUNT_PERCENTAGE,
              })
            )}
          </TotalPrice>
        )}
      </LineItemQuantityChanger>
    </Wrapper>
  );
}

LineItem.propTypes = {
  id: PropTypes.string.isRequired,
  highlightColor: PropTypes.string.isRequired,
  textColor: PropTypes.string.isRequired,
  variant: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    price: PropTypes.shape({
      amount: PropTypes.string,
    }),
    compareAtPrice: PropTypes.string,
    image: PropTypes.shape({
      src: PropTypes.string.isRequired,
    }).isRequired,
    product: PropTypes.shape({
      handle: PropTypes.string,
      productType: PropTypes.string,
      tags: PropTypes.arrayOf(PropTypes.string),
    }),
  }).isRequired,
  title: PropTypes.string.isRequired,
  quantity: PropTypes.number.isRequired,
  deleteLineItemHandler: PropTypes.func.isRequired,
  decreaseQuantityHandler: PropTypes.func.isRequired,
  increaseQuantityHandler: PropTypes.func.isRequired,
  customAttributes: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string,
    })
  ).isRequired,
};

export default LineItem;
